import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Spinner,
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Switch from "react-switch"; // Import the React Switch component
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  fetchData,
  uploadFile,
  putData,
  deleteData,
} from "../../services/apiServices";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDropzone } from "react-dropzone"; // For drag and drop functionality
import Cropper from "react-easy-crop"; // Replacing react-image-crop with react-easy-crop
import { postData } from "../../services/apiServices";

// Image Cropper Modal Component
const ImageCropperModal = ({ isOpen, toggle, image, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // When modal opens, reset the crop state
  useEffect(() => {
    if (isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPreviewUrl(null);
    }
  }, [isOpen]);

  // Handle crop complete
  const onCropCompleteCallback = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
    generatePreview(croppedAreaPixels);
  }, []);

  // Generate a preview from the crop selection
  const generatePreview = useCallback(
    async (croppedAreaPixels) => {
      if (!croppedAreaPixels || !image) return;

      try {
        const imageObj = new Image();
        imageObj.src = image;

        await new Promise((resolve) => {
          imageObj.onload = resolve;
        });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas dimensions to the cropped size
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        ctx.drawImage(
          imageObj,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        // Convert to data URL for preview
        const dataUrl = canvas.toDataURL("image/jpeg");
        setPreviewUrl(dataUrl);
      } catch (error) {
        console.error("Error generating preview:", error);
      }
    },
    [image]
  );

  // Create a crop preview and convert to blob for saving
  const createCropPreview = async () => {
    setLoading(true);

    try {
      if (!croppedAreaPixels || !image) {
        throw new Error("No valid crop selection");
      }

      const imageObj = new Image();
      imageObj.src = image;

      await new Promise((resolve) => {
        imageObj.onload = resolve;
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas size to exactly 256x256px for events
      canvas.width = 256;
      canvas.height = 256;

      ctx.drawImage(
        imageObj,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        256, // Target width
        256 // Target height
      );

      // Convert to blob
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error("Canvas is empty");
              return;
            }
            resolve(blob);
          },
          "image/jpeg",
          1
        );
      });
    } catch (error) {
      console.error("Error creating crop preview:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCrop = async () => {
    if (!croppedAreaPixels) {
      alert("Please make a crop selection first");
      return;
    }

    try {
      setLoading(true);
      const croppedImageBlob = await createCropPreview();
      onCropComplete(croppedImageBlob);
      toggle();
    } catch (error) {
      alert("Error processing the image. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Crop Image (Final size: 256x256px)
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md="8">
            <div
              className="crop-container"
              style={{ height: "400px", position: "relative" }}
            >
              {image ? (
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropCompleteCallback}
                  onZoomChange={setZoom}
                  cropShape="rect"
                  showGrid={true}
                />
              ) : (
                <div className="text-center p-4 border rounded">
                  <p>No image loaded. Please select an image first.</p>
                </div>
              )}
            </div>
            <div className="controls mt-3">
              <div className="d-flex align-items-center">
                <span className="me-2">Zoom</span>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="form-range"
                />
              </div>
              <div className="text-muted mt-2">
                {image
                  ? "Use the slider to zoom, drag to reposition, then click Apply Crop"
                  : "Image will appear here for cropping once selected."}
              </div>
            </div>
          </Col>
          <Col md="4">
            <div className="preview-container mt-3">
              <h6>Preview:</h6>
              {previewUrl ? (
                <div
                  className="square-preview"
                  style={{
                    width: "150px",
                    height: "150px",
                    overflow: "hidden",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                  }}
                >
                  <img
                    src={previewUrl}
                    alt="Crop preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: "150px",
                    height: "150px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span className="text-muted">No preview</span>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSaveCrop}
          disabled={loading || !croppedAreaPixels}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" /> Processing...
            </>
          ) : (
            "Apply Crop"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Region multi-select with chips component
const RegionMultiSelect = ({ regions, selectedRegions, onChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

  const handleRegionSelect = (regionId) => {
    const isSelected = selectedRegions.includes(regionId);
    let newSelectedRegions;

    if (isSelected) {
      // Remove the region if already selected
      newSelectedRegions = selectedRegions.filter((id) => id !== regionId);
    } else {
      // Add the region if not selected
      newSelectedRegions = [...selectedRegions, regionId];
    }

    onChange(newSelectedRegions);
  };

  const handleRemoveRegion = (regionId, e) => {
    e.stopPropagation(); // Prevent dropdown toggle
    const newSelectedRegions = selectedRegions.filter((id) => id !== regionId);
    onChange(newSelectedRegions);
  };

  // Get region names for selected IDs
  const getSelectedRegionNames = () => {
    return selectedRegions.map((regionId) => {
      const region = regions.find((r) => r._id === regionId);
      return region ? region.name : "";
    });
  };

  return (
    <div className="region-multiselect">
      <div
        className="form-control d-flex flex-wrap align-items-center"
        onClick={toggleDropdown}
        style={{
          cursor: "pointer",
          minHeight: "40px",
          height: "auto",
          position: "relative",
        }}
      >
        {selectedRegions.length > 0 ? (
          <div className="d-flex flex-wrap" style={{ maxWidth: "95%" }}>
            {selectedRegions.map((regionId) => {
              const region = regions.find((r) => r._id === regionId);
              return region ? (
                <div
                  key={region._id}
                  className="bg-primary text-white px-2 py-1 rounded d-flex align-items-center me-1 mb-1"
                >
                  <span>{region.name}</span>
                  <span
                    className="ms-1"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleRemoveRegion(region._id, e)}
                  >
                    ×
                  </span>
                </div>
              ) : null;
            })}
          </div>
        ) : (
          <div className="text-muted">Select regions...</div>
        )}
        <div className="dropdown-toggle ms-auto">
          <i className="mdi mdi-chevron-down"></i>
        </div>
      </div>

      <div
        className={`dropdown-menu w-100 ${dropdownOpen ? "show" : ""}`}
        style={{
          maxHeight: "200px",
          overflowY: "auto",
          display: dropdownOpen ? "block" : "none",
          width: "100%",
        }}
      >
        {regions.map((region) => (
          <div
            key={region._id}
            className="dropdown-item"
            onClick={() => handleRegionSelect(region._id)}
            style={{ cursor: "pointer" }}
          >
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={selectedRegions.includes(region._id)}
                onChange={() => {}} // Handled by the parent div click
                id={`region-${region._id}`}
              />
              <label
                className="form-check-label"
                htmlFor={`region-${region._id}`}
              >
                {region.name}
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Draggable Image Upload Component
const DraggableImageUpload = ({
  onFileSelect,
  currentImage,
  isUploading,
  uploadError,
  fieldId,
}) => {
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentImage || null);

  useEffect(() => {
    if (currentImage) {
      setPreviewUrl(currentImage);
    }
  }, [currentImage]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Instead of uploading right away, show the cropper
      const imageUrl = URL.createObjectURL(file);
      setSelectedFile(imageUrl);
      setShowCropper(true);
    }
  };

  const handleDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles[0]) {
      const file = acceptedFiles[0];
      // Create a preview and show the cropper
      const imageUrl = URL.createObjectURL(file);
      setSelectedFile(imageUrl);
      setShowCropper(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
    },
    multiple: false,
  });

  const handleCropComplete = (croppedBlob) => {
    // Create a file from the blob
    const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
      type: "image/jpeg",
    });

    // Create a preview URL for the cropped image
    const previewUrl = URL.createObjectURL(croppedBlob);
    setPreviewUrl(previewUrl);

    // Upload the cropped file
    onFileSelect(croppedFile);
  };

  const toggleCropper = () => {
    setShowCropper(!showCropper);
    if (!showCropper) {
      // Clean up the object URL when we close without saving
      URL.revokeObjectURL(selectedFile);
      setSelectedFile(null);
    }
  };

  return (
    <>
      <div
        {...getRootProps()}
        className={`dropzone-container p-3 border rounded ${
          isDragActive ? "border-primary" : ""
        }`}
        style={{
          borderStyle: "dashed",
          cursor: "pointer",
          backgroundColor: isDragActive ? "rgba(0, 123, 255, 0.1)" : "#fafafa",
          minHeight: "120px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <input {...getInputProps()} id={fieldId} disabled={isUploading} />

        {previewUrl ? (
          <div className="text-center">
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxHeight: "100px",
                maxWidth: "100%",
                marginBottom: "10px",
              }}
              className="img-thumbnail"
            />
            <p className="mb-0">Drag & drop a new image or click to replace</p>
          </div>
        ) : (
          <div className="text-center">
            {isDragActive ? (
              <p className="mb-0">Drop the image here...</p>
            ) : (
              <>
                <i
                  className="ri-upload-cloud-line"
                  style={{ fontSize: "2rem" }}
                ></i>
                <p className="mb-0">
                  Drag & drop an image here, or click to select
                </p>
                <p className="small text-muted">
                  (Image will be cropped to 1:1 ratio)
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {isUploading && (
        <div className="mt-2">
          <Spinner size="sm" /> Uploading...
        </div>
      )}

      {uploadError && (
        <Alert color="danger" className="mt-2">
          {uploadError}
        </Alert>
      )}

      {previewUrl && (
        <div className="mt-2 small text-muted">
          Click on the image area again to upload a different image
        </div>
      )}

      <div className="mt-2">
        <Label className="btn btn-outline-secondary btn-sm">
          Browse Files
          <Input
            type="file"
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept="image/*"
            disabled={isUploading}
          />
        </Label>
      </div>

      <ImageCropperModal
        isOpen={showCropper}
        toggle={toggleCropper}
        image={selectedFile}
        onCropComplete={handleCropComplete}
      />
    </>
  );
};

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [hasSubMarket, setHasSubMarket] = useState(false);
  const [subMarkets, setSubMarkets] = useState([]);
  const [singleMarketData, setSingleMarketData] = useState({
    start_date: "",
    end_date: "",
    can_close_early: false,
    settlement_time: 24,
    side_1: "Yes",
    side_2: "No",
    bond_amount: 50,
    resolution_window: 24,
  });
  const [uploadingFiles, setUploadingFiles] = useState({
    event_image: false,
    full_rules_doc: false,
  });
  const [uploadErrors, setUploadErrors] = useState({
    event_image: null,
    full_rules_doc: null,
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    event_image: null,
    full_rules_doc: null,
  });
  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    type: null, // 'event' or 'submarket'
    id: null,
    index: null,
    loading: false,
  });

  const [resolutionModal, setResolutionModal] = useState({
    show: false,
    marketId: null,
    marketIds: [], // Array of market IDs for resolving multiple markets
    isMultiResolve: false, // Flag to indicate if resolving multiple markets
    selectedWinningMarket: null, // ID of the market that will be resolved as "yes"
    loading: false,
    error: null,
    success: false,
  });

  const [resolutionForm, setResolutionForm] = useState({
    proposed_result: "yes", // Default value
    evidence_description: "",
    evidence_url_1: "",
    evidence_url_2: "",
  });

  // Fetch event data
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        const response = await fetchData(`api/event/events/${id}`);
        if (response && response.success) {
          setEvent(response.event);
          setHasSubMarket(response.event.has_sub_markets);

          if (response.event.has_sub_markets) {
            // Format sub markets data for multi-market events
            if (
              response.event.sub_markets &&
              response.event.sub_markets.length > 0
            ) {
              setSubMarkets(
                response.event.sub_markets.map((market) => ({
                  _id: market._id,
                  name: market.name,
                  event_image:
                    market.market_image || market.event_image || null,
                  list_date: formatDateForInput(market.list_date),
                  start_date: formatDateForInput(market.start_date),
                  end_date: formatDateForInput(market.end_date),
                  can_close_early: market.can_close_early || false,
                  settlement_time: market.settlement_time || 24,
                  status: market.status,
                  result: market.result,
                  side_1: market.side_1 || "Yes",
                  side_2: market.side_2 || "No",
                  bond_amount: market.bond_amount || 50,
                  resolution_window: market.resolution_window || 24,
                }))
              );
            }
          } else {
            // For single market events, get the market details from the first (and only) sub_market
            if (
              response.event.sub_markets &&
              response.event.sub_markets.length > 0
            ) {
              const market = response.event.sub_markets[0];
              setSingleMarketData({
                _id: market._id,
                start_date: formatDateForInput(market.start_date),
                end_date: formatDateForInput(market.end_date),
                can_close_early: market.can_close_early || false,
                settlement_time: market.settlement_time || 24,
                status: market.status || "open",
                side_1: market.side_1 || "Yes",
                side_2: market.side_2 || "No",
                bond_amount: market.bond_amount || 50,
                resolution_window: market.resolution_window || 24,
              });
            }
          }

          // Set uploaded files
          setUploadedFiles({
            event_image: response.event.event_image,
            full_rules_doc: response.event.full_rules_doc_url,
          });
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching event:", error);
        setLoading(false);
      }
    };

    fetchEventData();
  }, [id]);

  // Fetch categories
  useEffect(() => {
    const getCategories = async () => {
      try {
        const response = await fetchData("api/admin/categories");
        setCategories(response.data.categories || []);

        // Fetch subcategories if event data is loaded
        if (event && event.category) {
          // Find the selected category and its subcategories
          const selectedCategory = response.data.categories.find(
            (cat) => cat._id === event.category
          );
          if (
            selectedCategory &&
            selectedCategory.subcategories &&
            selectedCategory.subcategories.length > 0
          ) {
            setSubCategories(selectedCategory.subcategories);
          } else {
            // Fallback to API call if subcategories not embedded in category
            fetchSubCategories(event.category);
          }
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getCategories();
  }, [event]);

  // Fetch regions
  useEffect(() => {
    const getRegions = async () => {
      try {
        const response = await fetchData("api/admin/regions");
        if (response && response.data.regions) {
          setRegions(response.data.regions || []);
        }
      } catch (error) {
        console.error("Error fetching regions:", error);
      }
    };
    getRegions();
  }, []);

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await fetchData(`api/admin/subcategories/${categoryId}`);
      setSubCategories(response.sub_categories || []);
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
    }
  };

  // Format date for datetime-local input (display as UTC)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    // Since the date from API is already in UTC, display it directly as UTC
    // Use UTC methods to extract the date components without timezone conversion
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`; // Format: YYYY-MM-DDTHH:MM
  };

  // Format date in UTC format for API calls
  const formatDateForUTC = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString(); // Return ISO string in UTC
  };

  const handleFileUpload = async (file, field, submarketIndex = null) => {
    if (!file) return;

    const uploadField =
      submarketIndex !== null ? `submarket_${submarketIndex}` : field;

    try {
      setUploadingFiles((prev) => ({ ...prev, [uploadField]: true }));
      setUploadErrors((prev) => ({ ...prev, [uploadField]: null }));

      const fileType = field === "full_rules_doc" ? "document" : "image";
      const fileUrl = await uploadFile(file, fileType);

      if (submarketIndex !== null) {
        const newSubMarkets = [...subMarkets];
        newSubMarkets[submarketIndex] = {
          ...newSubMarkets[submarketIndex],
          event_image: fileUrl,
        };
        setSubMarkets(newSubMarkets);
      } else {
        setUploadedFiles((prev) => ({ ...prev, [field]: fileUrl }));
        validation.setFieldValue(field, fileUrl);
      }
    } catch (error) {
      const errorMessage = error.message || "Error uploading file";
      setUploadErrors((prev) => ({ ...prev, [uploadField]: errorMessage }));
      if (submarketIndex === null) {
        validation.setFieldError(field, errorMessage);
      }
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [uploadField]: false }));
    }
  };

  const formatEventData = (values) => {
    const eventData = {
      event_title: values.event_title,
      event_image: values.event_image,
      market_summary: values.market_summary,
      category: values.category,
      sub_category: values.sub_category,
      has_sub_markets: hasSubMarket,
      list_date: formatDateForUTC(values.list_date),
      regions: values.regions || [],
      rules_summary: values.rules_summary,
      rules_variables:
        values.rules_variables?.split(",").map((v) => v.trim()) || [],
      settlement_sources:
        values.settlement_sources?.split(",").map((s) => s.trim()) || [],
      full_rules_doc_url: values.full_rules_doc,
      is_live: values.is_live,
    };

    if (hasSubMarket && subMarkets.length > 0) {
      // Multiple submarkets - use 'markets' field for API compatibility
      eventData.markets = subMarkets.map((market) => {
        const marketData = {
          name: market.name,
          market_image: market.event_image,
          list_date: formatDateForUTC(market.list_date),
          start_date: formatDateForUTC(market.start_date),
          end_date: formatDateForUTC(market.end_date),
          can_close_early: market.can_close_early,
          settlement_time: Number(market.settlement_time),
          side_1: market.side_1 || "Yes",
          side_2: market.side_2 || "No",
          bond_amount: Number(market.bond_amount),
          resolution_window: Number(market.resolution_window),
        };

        // Include ID if it exists (for existing markets)
        if (market._id) {
          marketData._id = market._id;
        }

        return marketData;
      });
    } else {
      // Single market (non-submarket event)
      const singleMarket = {
        name: values.event_title,
        market_image: values.event_image,
        list_date: formatDateForUTC(values.list_date),
        start_date: formatDateForUTC(singleMarketData.start_date),
        end_date: formatDateForUTC(singleMarketData.end_date),
        can_close_early: singleMarketData.can_close_early, // Use directly from singleMarketData
        settlement_time: Number(singleMarketData.settlement_time),
        side_1: singleMarketData.side_1 || "Yes",
        side_2: singleMarketData.side_2 || "No",
        bond_amount: Number(singleMarketData.bond_amount),
        resolution_window: Number(singleMarketData.resolution_window),
      };

      // Include market ID if it exists
      if (singleMarketData._id) {
        singleMarket._id = singleMarketData._id;
      }

      eventData.markets = [singleMarket];
    }

    return eventData;
  };

  const initialValues = {
    event_title: event?.event_title || "",
    event_image: event?.event_image || null,
    market_summary: event?.market_summary || "", // Add event summary field
    category: event?.category || "",
    sub_category: event?.sub_category || "",
    list_date: event?.list_date ? formatDateForInput(event.list_date) : "",
    regions: event?.regions || [], // Ensure regions array is set from event data
    rules_summary: event?.rules_summary || "",
    rules_variables: event?.rules_variables
      ? event.rules_variables.join(", ")
      : "",
    settlement_sources: event?.settlement_sources
      ? event.settlement_sources.join(", ")
      : "",
    full_rules_doc: event?.full_rules_doc_url || null,
    is_live: event?.is_live || false,
    start_date: singleMarketData.start_date || "",
    end_date: singleMarketData.end_date || "",
    can_close_early: singleMarketData.can_close_early || false,
    settlement_time: singleMarketData.settlement_time || 24,
    side_1: singleMarketData.side_1 || "Yes",
    side_2: singleMarketData.side_2 || "No",
    bond_amount: singleMarketData.bond_amount || 50,
    resolution_window: singleMarketData.resolution_window || 24,
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      event_title: Yup.string().required("Event title is required"),
      event_image: Yup.string().required("Event image is required"),
      market_summary: Yup.string().required("Event summary is required"), // Add event summary validation
      category: Yup.string().required("Category is required"),
      list_date: Yup.string().required("Listing date is required"),
      rules_summary: Yup.string().required("Rules summary is required"),
      settlement_sources: Yup.string().required(
        "Settlement sources are required"
      ),
      full_rules_doc: Yup.string().required("Rules document is required"),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitStatus({ loading: true, error: null, success: false });
        const eventData = formatEventData(values);
        console.log("Updating event data:", eventData);
        const response = await putData(`api/event/events/${id}`, eventData);
        console.log("Event updated successfully:", response);
        setSubmitStatus({ loading: false, error: null, success: true });

        // Refresh event data
        const updatedEvent = await fetchData(`api/event/events/${id}`);
        if (updatedEvent && updatedEvent.success) {
          setEvent(updatedEvent.event);

          // Update single market data if not using submarkets
          if (
            !hasSubMarket &&
            updatedEvent.event.sub_markets &&
            updatedEvent.event.sub_markets[0]
          ) {
            const market = updatedEvent.event.sub_markets[0];
            setSingleMarketData({
              _id: market._id,
              start_date: formatDateForInput(market.start_date),
              end_date: formatDateForInput(market.end_date),
              can_close_early: market.can_close_early,
              settlement_time: market.settlement_time,
              status: market.status,
            });
          }
        }
      } catch (error) {
        console.error("Error updating event:", error);
        setSubmitStatus({
          loading: false,
          error:
            error.response?.data?.message ||
            error.message ||
            "Failed to update event",
          success: false,
        });
      }
    },
  });

  const handleSubMarketChange = (index, field, value) => {
    const newSubMarkets = [...subMarkets];
    newSubMarkets[index] = { ...newSubMarkets[index], [field]: value };
    setSubMarkets(newSubMarkets);
  };

  const addSubMarket = () => {
    setSubMarkets([
      ...subMarkets,
      {
        name: "",
        event_image: null,
        list_date: validation.values.list_date,
        start_date: "",
        end_date: "",
        can_close_early: false,
        settlement_time: 24,
        side_1: "Yes",
        side_2: "No",
        bond_amount: 50,
        resolution_window: 24,
      },
    ]);
  };

  const removeSubMarket = (index) => {
    const newSubMarkets = subMarkets.filter((_, i) => i !== index);
    setSubMarkets(newSubMarkets);
  };

  const handleSingleMarketChange = (field, value) => {
    setSingleMarketData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Event deletion handler
  const handleDeleteEvent = async () => {
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      await deleteData(`api/event/events/${id}`);
      setDeleteModal({
        show: false,
        type: null,
        id: null,
        index: null,
        loading: false,
      });
      navigate("/events", {
        state: { message: "Event deleted successfully!" },
      });
    } catch (error) {
      console.error("Error deleting event:", error);
      setDeleteModal((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to delete event",
      }));
    }
  };

  // Submarket deletion handler
  const handleDeleteSubmarket = async () => {
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));

      if (deleteModal.id) {
        await deleteData(`markets/${deleteModal.id}`);

        // Remove the submarket from local state
        const newSubMarkets = subMarkets.filter(
          (_, index) => index !== deleteModal.index
        );
        setSubMarkets(newSubMarkets);

        setDeleteModal({
          show: false,
          type: null,
          id: null,
          index: null,
          loading: false,
        });
        setSubmitStatus({
          loading: false,
          error: null,
          success: true,
          message: "Submarket deleted successfully!",
        });

        setTimeout(() => {
          setSubmitStatus({ loading: false, error: null, success: false });
        }, 3000);
      }
    } catch (error) {
      console.error("Error deleting submarket:", error);
      setDeleteModal((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to delete submarket",
      }));
    }
  };

  // Toggle delete confirmation modal
  const toggleDeleteModal = (type = null, id = null, index = null) => {
    setDeleteModal({
      show: !deleteModal.show,
      type,
      id,
      index,
      loading: false,
      error: null,
    });
  };

  // Toggle resolution modal
  const toggleResolutionModal = (marketId = null, isMultiResolve = false) => {
    // If opening the modal for multi-resolution
    if (!resolutionModal.show && isMultiResolve) {
      // Get all open market IDs
      let openMarketIds = [];

      if (hasSubMarket) {
        // For events with submarkets
        openMarketIds = subMarkets
          .filter((market) => market.status === "open")
          .map((market) => market._id);
      } else if (singleMarketData.status === "open") {
        // For single market events
        openMarketIds = [singleMarketData._id];
      }

      setResolutionModal({
        show: true,
        marketId: null,
        marketIds: openMarketIds,
        isMultiResolve: true,
        selectedWinningMarket:
          openMarketIds.length > 0 ? openMarketIds[0] : null,
        loading: false,
        error: null,
        success: false,
      });
    } else {
      // For single market resolution or closing the modal
      setResolutionModal({
        show: !resolutionModal.show,
        marketId,
        marketIds: [],
        isMultiResolve: false,
        selectedWinningMarket: null,
        loading: false,
        error: null,
        success: false,
      });
    }

    // Reset form if closing the modal
    if (resolutionModal.show) {
      setResolutionForm({
        proposed_result: "yes",
        evidence_description: "",
        evidence_url_1: "",
        evidence_url_2: "",
      });
    }
  };

  // Handle resolution form changes
  const handleResolutionFormChange = (field, value) => {
    setResolutionForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle winning market selection for multi-resolution
  const handleWinningMarketChange = (marketId) => {
    setResolutionModal((prev) => ({
      ...prev,
      selectedWinningMarket: marketId,
    }));
  };

  // Submit resolution proposal
  const handleSubmitResolution = async () => {
    try {
      setResolutionModal((prev) => ({ ...prev, loading: true }));

      if (resolutionModal.isMultiResolve) {
        // Multi-market resolution
        const successfulResolutions = [];
        const failedResolutions = [];

        // Process each market in sequence
        for (const marketId of resolutionModal.marketIds) {
          try {
            const isWinner = marketId === resolutionModal.selectedWinningMarket;

            const payload = {
              market_id: marketId,
              ...resolutionForm,
              proposed_result: isWinner ? "yes" : "no", // Set result based on winning market
            };

            console.log(
              `Submitting resolution for market ${marketId}:`,
              payload
            );

            // Make API call using the postData function
            const response = await postData(
              "resolution/admin-propose",
              payload
            );

            if (!response || response.error) {
              throw new Error(
                response?.error || "Failed to propose resolution"
              );
            }

            successfulResolutions.push(marketId);
          } catch (error) {
            console.error(`Error resolving market ${marketId}:`, error);
            failedResolutions.push({ marketId, error: error.message });
          }
        }

        if (failedResolutions.length > 0) {
          // Some resolutions failed
          const errorMessage = `Failed to resolve ${failedResolutions.length} markets. ${successfulResolutions.length} markets were resolved successfully.`;
          setResolutionModal((prev) => ({
            ...prev,
            loading: false,
            error: errorMessage,
            success: successfulResolutions.length > 0,
          }));
        } else {
          // All resolutions succeeded
          setResolutionModal((prev) => ({
            ...prev,
            loading: false,
            success: true,
            error: null,
          }));
        }
      } else {
        // Single market resolution
        const payload = {
          market_id: resolutionModal.marketId,
          ...resolutionForm,
        };

        console.log("Submitting resolution:", payload);

        // Make API call using the postData function
        const response = await postData("resolution/admin-propose", payload);

        if (!response || response.error) {
          throw new Error(response?.error || "Failed to propose resolution");
        }

        setResolutionModal((prev) => ({
          ...prev,
          loading: false,
          success: true,
          error: null,
        }));
      }

      // Close modal after 2 seconds and reload event data
      setTimeout(() => {
        toggleResolutionModal();
        // Refresh event data
        const fetchEventData = async () => {
          try {
            const response = await fetchData(`events/${id}`);
            if (response && response.success) {
              setEvent(response.event);
              // Also update the market status for submarkets or single market
              if (hasSubMarket) {
                if (
                  response.event.sub_markets &&
                  response.event.sub_markets.length > 0
                ) {
                  setSubMarkets(
                    response.event.sub_markets.map((market) => ({
                      _id: market._id,
                      name: market.name,
                      event_image:
                        market.market_image || market.event_image || null,
                      list_date: formatDateForInput(market.list_date),
                      start_date: formatDateForInput(market.start_date),
                      end_date: formatDateForInput(market.end_date),
                      can_close_early: market.can_close_early || false,
                      settlement_time: market.settlement_time || 24,
                      status: market.status,
                      result: market.result,
                    }))
                  );
                }
              } else {
                // For single market events, get the market details from the first (and only) sub_market
                if (
                  response.event.sub_markets &&
                  response.event.sub_markets.length > 0
                ) {
                  const market = response.event.sub_markets[0];
                  setSingleMarketData({
                    _id: market._id,
                    start_date: formatDateForInput(market.start_date),
                    end_date: formatDateForInput(market.end_date),
                    can_close_early: market.can_close_early || false,
                    settlement_time: market.settlement_time || 24,
                    status: market.status || "open",
                  });
                }
              }
            }
          } catch (error) {
            console.error("Error fetching event:", error);
          }
        };
        fetchEventData();
      }, 2000);
    } catch (error) {
      console.error("Error submitting resolution:", error);
      setResolutionModal((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
        success: false,
      }));
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
            <p className="mt-2">Loading event details...</p>
          </div>
        </Container>
      </div>
    );
  }

  // Handle category change
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    validation.handleChange(e);

    if (!categoryId) {
      setSubCategories([]);
      return;
    }

    // Find the selected category and its subcategories
    const selectedCategory = categories.find((cat) => cat._id === categoryId);
    if (
      selectedCategory &&
      selectedCategory.subcategories &&
      selectedCategory.subcategories.length > 0
    ) {
      setSubCategories(selectedCategory.subcategories);
    } else {
      // Fallback to API call if subcategories not embedded in category
      fetchSubCategories(categoryId);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-4 d-flex justify-content-between align-items-center">
          <div>
            <Breadcrumb>
              <BreadcrumbItem>
                <Link to="/">Home</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <Link to="/events">Events</Link>
              </BreadcrumbItem>
              <BreadcrumbItem active>Edit Event</BreadcrumbItem>
            </Breadcrumb>
            <h4 className="mb-0">Edit Event</h4>
            <p className="text-muted mt-1">
              Editing event: {event?.event_title}
            </p>
          </div>
          <div className="d-flex">
            {!hasSubMarket && singleMarketData.status === "open" && (
              <Button
                color="primary"
                className="me-2"
                onClick={() => toggleResolutionModal(singleMarketData._id)}
              >
                Propose Resolution
              </Button>
            )}
            <Button
              color="danger"
              onClick={() => toggleDeleteModal("event", id)}
            >
              Delete Event
            </Button>
          </div>
        </div>

        <Form onSubmit={validation.handleSubmit}>
          <Card>
            <CardBody>
              {submitStatus.success && (
                <Alert color="success" className="mb-4">
                  {submitStatus.message || "Event updated successfully!"}
                </Alert>
              )}
              {submitStatus.error && (
                <Alert color="danger" className="mb-4">
                  {submitStatus.error}
                </Alert>
              )}

              {/* Event details form fields */}
              <Row>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="event_title">Event Name</Label>
                    <Input
                      type="text"
                      id="event_title"
                      name="event_title"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.event_title}
                      invalid={
                        validation.touched.event_title &&
                        validation.errors.event_title
                      }
                    />
                    {validation.touched.event_title &&
                      validation.errors.event_title && (
                        <div className="text-danger">
                          {validation.errors.event_title}
                        </div>
                      )}
                  </div>
                </Col>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="event_image">Event Image</Label>
                    <DraggableImageUpload
                      onFileSelect={(file) =>
                        handleFileUpload(file, "event_image")
                      }
                      currentImage={
                        uploadedFiles.event_image ||
                        validation.values.event_image
                      }
                      isUploading={uploadingFiles.event_image}
                      uploadError={uploadErrors.event_image}
                      fieldId="event_image_upload"
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="12">
                  <div className="mb-3">
                    <Label htmlFor="market_summary">Market Summary</Label>
                    <Input
                      type="textarea"
                      id="market_summary"
                      name="market_summary"
                      onChange={validation.handleChange}
                      value={validation.values.market_summary}
                      placeholder="Enter a short summary about this event"
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="category">Category</Label>
                    <Input
                      type="select"
                      id="category"
                      name="category"
                      onChange={handleCategoryChange}
                      value={validation.values.category}
                      invalid={
                        validation.touched.category &&
                        validation.errors.category
                      }
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </Input>
                    {validation.touched.category &&
                      validation.errors.category && (
                        <div className="text-danger">
                          {validation.errors.category}
                        </div>
                      )}
                  </div>
                </Col>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="sub_category">Sub Category</Label>
                    <Input
                      type="select"
                      id="sub_category"
                      name="sub_category"
                      onChange={validation.handleChange}
                      value={validation.values.sub_category}
                    >
                      <option value="">Select Sub Category</option>
                      {subCategories.map((subCategory) => (
                        <option key={subCategory._id} value={subCategory._id}>
                          {subCategory.name}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="list_date">
                      Listing Date (UTC Timezone)
                    </Label>
                    <small className="form-text text-muted d-block">
                      From this Date the event will be visible to users. (Times
                      displayed in UTC timezone)
                    </small>
                    <Input
                      type="datetime-local"
                      id="list_date"
                      name="list_date"
                      onChange={validation.handleChange}
                      value={validation.values.list_date}
                      invalid={
                        validation.touched.list_date &&
                        validation.errors.list_date
                      }
                    />
                    {validation.touched.list_date &&
                      validation.errors.list_date && (
                        <div className="text-danger">
                          {validation.errors.list_date}
                        </div>
                      )}
                  </div>
                </Col>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="regions">Regions</Label>
                    <RegionMultiSelect
                      regions={regions}
                      selectedRegions={validation.values.regions || []}
                      onChange={(selectedRegions) =>
                        validation.setFieldValue("regions", selectedRegions)
                      }
                    />
                    {validation.touched.regions &&
                      validation.errors.regions && (
                        <div className="text-danger">
                          {validation.errors.regions}
                        </div>
                      )}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="12">
                  <div className="mb-3">
                    <Label htmlFor="settlement_sources">
                      Settlement Sources
                    </Label>
                    <Input
                      type="textarea"
                      id="settlement_sources"
                      name="settlement_sources"
                      onChange={validation.handleChange}
                      value={validation.values.settlement_sources}
                      placeholder="Enter settlement sources (comma separated)"
                      invalid={
                        validation.touched.settlement_sources &&
                        validation.errors.settlement_sources
                      }
                    />
                    {validation.touched.settlement_sources &&
                      validation.errors.settlement_sources && (
                        <div className="text-danger">
                          {validation.errors.settlement_sources}
                        </div>
                      )}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="12">
                  <div className="mb-3">
                    <Label htmlFor="rules_summary">Rules</Label>
                    <Input
                      type="textarea"
                      id="rules_summary"
                      name="rules_summary"
                      onChange={validation.handleChange}
                      value={validation.values.rules_summary}
                      invalid={
                        validation.touched.rules_summary &&
                        validation.errors.rules_summary
                      }
                    />
                    {validation.touched.rules_summary &&
                      validation.errors.rules_summary && (
                        <div className="text-danger">
                          {validation.errors.rules_summary}
                        </div>
                      )}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="full_rules_doc">Full Rules PDF</Label>
                    <Input
                      type="file"
                      id="full_rules_doc"
                      name="full_rules_doc"
                      onChange={(e) =>
                        handleFileUpload(e.target.files[0], "full_rules_doc")
                      }
                      accept=".pdf"
                      disabled={uploadingFiles.full_rules_doc}
                    />
                    {uploadingFiles.full_rules_doc && (
                      <div className="mt-2">
                        <Spinner size="sm" /> Uploading...
                      </div>
                    )}
                    {uploadErrors.full_rules_doc && (
                      <Alert color="danger" className="mt-2">
                        {uploadErrors.full_rules_doc}
                      </Alert>
                    )}
                    {(uploadedFiles.full_rules_doc ||
                      validation.values.full_rules_doc) && (
                      <div className="mt-2">
                        <Alert color="success">
                          PDF uploaded successfully!{" "}
                          <a
                            href={
                              uploadedFiles.full_rules_doc ||
                              validation.values.full_rules_doc
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View PDF
                          </a>
                        </Alert>
                      </div>
                    )}
                    {validation.touched.full_rules_doc &&
                      validation.errors.full_rules_doc && (
                        <div className="text-danger">
                          {validation.errors.full_rules_doc}
                        </div>
                      )}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <div className="mb-3">
                    <Label className="d-block mb-2" htmlFor="has_sub_markets">
                      Has Submarket (cannot be changed after creation)
                    </Label>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Switch
                        checked={hasSubMarket}
                        onChange={(checked) => setHasSubMarket(checked)}
                        onColor="#86d3ff"
                        onHandleColor="#2693e6"
                        offColor="#cccccc"
                        offHandleColor="#888888"
                        handleDiameter={24}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        height={20}
                        width={48}
                        id="has_sub_markets_switch"
                        className="me-2"
                        disabled={true} // Disabling the toggle once event is created
                      />
                      <span>{hasSubMarket ? "Yes" : "No"}</span>
                    </div>
                  </div>
                </Col>
              </Row>

              {hasSubMarket ? (
                <div>
                  {/* Check if there are any open markets to resolve */}
                  {subMarkets.filter((market) => market.status === "open")
                    .length > 1 && (
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        color="primary"
                        onClick={() => toggleResolutionModal(null, true)}
                      >
                        Resolve All Markets
                      </Button>
                    </div>
                  )}
                  {subMarkets.map((subMarket, index) => (
                    <Card key={index} className="mb-3">
                      <CardBody>
                        <Row>
                          <Col lg="10">
                            <h5>Submarket {index + 1}</h5>
                          </Col>
                          <Col lg="2" className="text-end">
                            {subMarket.status === "open" && (
                              <Button
                                color="primary"
                                size="sm"
                                className="me-2"
                                onClick={() =>
                                  toggleResolutionModal(subMarket._id)
                                }
                              >
                                Propose Resolution
                              </Button>
                            )}
                            {subMarkets.length > 1 && (
                              <Button
                                color="danger"
                                size="sm"
                                className="me-2"
                                onClick={() =>
                                  toggleDeleteModal(
                                    "submarket",
                                    subMarket._id,
                                    index
                                  )
                                }
                              >
                                Delete
                              </Button>
                            )}
                            {subMarkets.length > 1 && (
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeSubMarket(index)}
                              >
                                ✕
                              </Button>
                            )}
                          </Col>
                        </Row>

                        {/* Submarket form fields */}
                        <Row>
                          <Col lg="6">
                            <div className="mb-3">
                              <Label>Submarket Name</Label>
                              <Input
                                type="text"
                                value={subMarket.name}
                                onChange={(e) =>
                                  handleSubMarketChange(
                                    index,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg="6">
                            <div className="mb-3">
                              <Label>Submarket Image</Label>
                              <DraggableImageUpload
                                onFileSelect={(file) =>
                                  handleFileUpload(file, "image", index)
                                }
                                currentImage={subMarket.event_image}
                                isUploading={
                                  uploadingFiles[`submarket_${index}`]
                                }
                                uploadError={uploadErrors[`submarket_${index}`]}
                                fieldId={`submarket_image_${index}`}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="6">
                            <div className="mb-3">
                              <Label>Start Date (UTC Timezone)</Label>
                              <small className="form-text text-muted d-block">
                                From this Date the users can place orders.
                                (Select time in UTC timezone)
                              </small>
                              <Input
                                type="datetime-local"
                                value={subMarket.start_date}
                                onChange={(e) =>
                                  handleSubMarketChange(
                                    index,
                                    "start_date",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg="6">
                            <div className="mb-3">
                              <Label>End Date (UTC Timezone)</Label>
                              <small className="form-text text-muted d-block">
                                From this Date the users can not place orders.
                                (Select time in UTC timezone)
                              </small>
                              <Input
                                type="datetime-local"
                                value={subMarket.end_date}
                                onChange={(e) =>
                                  handleSubMarketChange(
                                    index,
                                    "end_date",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="6">
                            <div className="mb-3">
                              <Label
                                className="d-block mb-2"
                                htmlFor="can_close_early"
                              >
                                Can close early?
                              </Label>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Switch
                                  checked={subMarket.can_close_early}
                                  onChange={(checked) => {
                                    console.log("Toggle switched to:", checked);
                                    handleSubMarketChange(
                                      index,
                                      "can_close_early",
                                      checked
                                    );
                                  }}
                                  onColor="#86d3ff"
                                  onHandleColor="#2693e6"
                                  offColor="#cccccc"
                                  offHandleColor="#888888"
                                  handleDiameter={24}
                                  uncheckedIcon={false}
                                  checkedIcon={false}
                                  height={20}
                                  width={48}
                                  className="me-2"
                                />
                                <span>
                                  {subMarket.can_close_early ? "Yes" : "No"}
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="6">
                            <div className="mb-3">
                              <Label>Side 1</Label>
                              <Input
                                type="text"
                                value={subMarket.side_1}
                                onChange={(e) =>
                                  handleSubMarketChange(
                                    index,
                                    "side_1",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Side 1 description"
                              />
                            </div>
                          </Col>
                          <Col lg="6">
                            <div className="mb-3">
                              <Label>Side 2</Label>
                              <Input
                                type="text"
                                value={subMarket.side_2}
                                onChange={(e) =>
                                  handleSubMarketChange(
                                    index,
                                    "side_2",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Side 2 description"
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="4">
                            <div className="mb-3">
                              <Label>Bond Amount ($)</Label>
                              <Input
                                type="number"
                                value={subMarket.bond_amount}
                                onChange={(e) =>
                                  handleSubMarketChange(
                                    index,
                                    "bond_amount",
                                    Number(e.target.value)
                                  )
                                }
                                min="0"
                                placeholder="Bond amount in USD"
                              />
                            </div>
                          </Col>
                          <Col lg="4">
                            <div className="mb-3">
                              <Label>Settlement Time (hours)</Label>
                              <Input
                                type="number"
                                value={subMarket.settlement_time}
                                onChange={(e) =>
                                  handleSubMarketChange(
                                    index,
                                    "settlement_time",
                                    Number(e.target.value)
                                  )
                                }
                                min="1"
                                placeholder="Settlement time in hours"
                              />
                            </div>
                          </Col>
                          <Col lg="4">
                            <div className="mb-3">
                              <Label>Resolution Window (hours)</Label>
                              <Input
                                type="number"
                                value={subMarket.resolution_window}
                                onChange={(e) =>
                                  handleSubMarketChange(
                                    index,
                                    "resolution_window",
                                    Number(e.target.value)
                                  )
                                }
                                min="1"
                                placeholder="Resolution window in hours"
                              />
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  ))}
                  <Button
                    color="primary"
                    onClick={addSubMarket}
                    className="mb-3"
                  >
                    Add Submarket
                  </Button>
                </div>
              ) : (
                <div>
                  {/* Single market form */}
                  <Card className="mb-3">
                    <CardBody>
                      <h5>Market Details</h5>
                      <p className="text-muted">
                        These settings apply to the single market for this
                        event.
                      </p>

                      <Row>
                        <Col lg="6">
                          <div className="mb-3">
                            <Label htmlFor="start_date">
                              Start Date (UTC Timezone)
                            </Label>
                            <small className="form-text text-muted d-block">
                              From this Date the users can place orders. (Select
                              time in UTC timezone)
                            </small>
                            <Input
                              type="datetime-local"
                              id="start_date"
                              name="start_date"
                              value={singleMarketData.start_date}
                              onChange={(e) =>
                                handleSingleMarketChange(
                                  "start_date",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </Col>
                        <Col lg="6">
                          <div className="mb-3">
                            <Label htmlFor="end_date">
                              End Date (UTC Timezone)
                            </Label>
                            <small className="form-text text-muted d-block">
                              From this Date the users can not place orders.
                              (Select time in UTC timezone)
                            </small>
                            <Input
                              type="datetime-local"
                              id="end_date"
                              name="end_date"
                              value={singleMarketData.end_date}
                              onChange={(e) =>
                                handleSingleMarketChange(
                                  "end_date",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg="6">
                          <div className="mb-3">
                            <Label
                              className="d-block mb-2"
                              htmlFor="can_close_early"
                            >
                              Can close early?
                            </Label>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <Switch
                                checked={singleMarketData.can_close_early}
                                onChange={(checked) => {
                                  console.log("Toggle switched to:", checked);
                                  setSingleMarketData((prev) => ({
                                    ...prev,
                                    can_close_early: checked,
                                  }));
                                }}
                                onColor="#86d3ff"
                                onHandleColor="#2693e6"
                                offColor="#cccccc"
                                offHandleColor="#888888"
                                handleDiameter={24}
                                uncheckedIcon={false}
                                checkedIcon={false}
                                height={20}
                                width={48}
                                id="can_close_early_switch"
                                className="me-2"
                              />
                              <span>
                                {singleMarketData.can_close_early
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg="6">
                          <div className="mb-3">
                            <Label htmlFor="side_1">Side 1</Label>
                            <Input
                              type="text"
                              id="side_1"
                              name="side_1"
                              value={singleMarketData.side_1}
                              onChange={(e) =>
                                handleSingleMarketChange(
                                  "side_1",
                                  e.target.value
                                )
                              }
                              placeholder="Enter Side 1 description"
                            />
                          </div>
                        </Col>
                        <Col lg="6">
                          <div className="mb-3">
                            <Label htmlFor="side_2">Side 2</Label>
                            <Input
                              type="text"
                              id="side_2"
                              name="side_2"
                              value={singleMarketData.side_2}
                              onChange={(e) =>
                                handleSingleMarketChange(
                                  "side_2",
                                  e.target.value
                                )
                              }
                              placeholder="Enter Side 2 description"
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg="4">
                          <div className="mb-3">
                            <Label htmlFor="bond_amount">Bond Amount ($)</Label>
                            <Input
                              type="number"
                              id="bond_amount"
                              name="bond_amount"
                              value={singleMarketData.bond_amount}
                              onChange={(e) =>
                                handleSingleMarketChange(
                                  "bond_amount",
                                  Number(e.target.value)
                                )
                              }
                              min="0"
                              placeholder="Bond amount in USD"
                            />
                          </div>
                        </Col>
                        <Col lg="4">
                          <div className="mb-3">
                            <Label htmlFor="settlement_time_new">
                              Settlement Time (hours)
                            </Label>
                            <Input
                              type="number"
                              id="settlement_time_new"
                              name="settlement_time"
                              value={singleMarketData.settlement_time}
                              onChange={(e) =>
                                handleSingleMarketChange(
                                  "settlement_time",
                                  Number(e.target.value)
                                )
                              }
                              min="1"
                              placeholder="Settlement time in hours"
                            />
                          </div>
                        </Col>
                        <Col lg="4">
                          <div className="mb-3">
                            <Label htmlFor="resolution_window">
                              Resolution Window (hours)
                            </Label>
                            <Input
                              type="number"
                              id="resolution_window"
                              name="resolution_window"
                              value={singleMarketData.resolution_window}
                              onChange={(e) =>
                                handleSingleMarketChange(
                                  "resolution_window",
                                  Number(e.target.value)
                                )
                              }
                              min="1"
                              placeholder="Resolution window in hours"
                            />
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </div>
              )}

              <Row className="mt-4">
                <Col>
                  <div className="d-flex justify-content-between">
                    <Button
                      color="secondary"
                      onClick={() => navigate("/events")}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={
                        submitStatus.loading ||
                        Object.values(uploadingFiles).some(Boolean)
                      }
                    >
                      {submitStatus.loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Updating Event...
                        </>
                      ) : Object.values(uploadingFiles).some(Boolean) ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Uploading...
                        </>
                      ) : (
                        "Update Event"
                      )}
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.show}
        toggle={() => toggleDeleteModal()}
        centered
      >
        <ModalHeader toggle={() => toggleDeleteModal()}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          {deleteModal.type === "event" ? (
            <>
              <p>Are you sure you want to delete this event?</p>
              <p className="text-danger mb-0">
                This will delete the event and all associated markets. This
                action cannot be undone.
              </p>
            </>
          ) : (
            <>
              <p>Are you sure you want to delete this submarket?</p>
              <p className="text-danger mb-0">This action cannot be undone.</p>
            </>
          )}
          {deleteModal.error && (
            <Alert color="danger" className="mt-3 mb-0">
              {deleteModal.error}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => toggleDeleteModal()}
            disabled={deleteModal.loading}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={
              deleteModal.type === "event"
                ? handleDeleteEvent
                : handleDeleteSubmarket
            }
            disabled={deleteModal.loading}
          >
            {deleteModal.loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Resolution Proposal Modal */}
      <Modal
        isOpen={resolutionModal.show}
        toggle={() => toggleResolutionModal()}
        centered
      >
        <ModalHeader toggle={() => toggleResolutionModal()}>
          {resolutionModal.isMultiResolve
            ? "Resolve All Markets"
            : "Propose Resolution"}
        </ModalHeader>
        <ModalBody>
          {resolutionModal.success ? (
            <Alert color="success" className="mb-0">
              Resolution proposed successfully!
            </Alert>
          ) : (
            <>
              <p>
                {resolutionModal.isMultiResolve
                  ? 'Please select which market should be resolved as "Yes". All other markets will be resolved as "No".'
                  : "Please provide the details for the proposed resolution."}
              </p>

              {resolutionModal.isMultiResolve ? (
                <div className="mb-3">
                  <Label for="winning_market">Select Winning Market</Label>
                  <Input
                    type="select"
                    id="winning_market"
                    value={resolutionModal.selectedWinningMarket || ""}
                    onChange={(e) => handleWinningMarketChange(e.target.value)}
                  >
                    {hasSubMarket ? (
                      subMarkets
                        .filter((market) => market.status === "open")
                        .map((market) => (
                          <option key={market._id} value={market._id}>
                            {market.name}
                          </option>
                        ))
                    ) : (
                      <option value={singleMarketData._id}>
                        {event?.event_title}
                      </option>
                    )}
                  </Input>
                  <small className="text-muted">
                    This market will be resolved as "Yes". All other open
                    markets will be resolved as "No".
                  </small>
                </div>
              ) : (
                <div className="mb-3">
                  <Label for="proposed_result">Proposed Result</Label>
                  <Input
                    type="select"
                    id="proposed_result"
                    value={resolutionForm.proposed_result}
                    onChange={(e) =>
                      handleResolutionFormChange(
                        "proposed_result",
                        e.target.value
                      )
                    }
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Input>
                </div>
              )}

              <div className="mb-3">
                <Label for="evidence_description">Evidence Description</Label>
                <Input
                  type="textarea"
                  id="evidence_description"
                  value={resolutionForm.evidence_description}
                  onChange={(e) =>
                    handleResolutionFormChange(
                      "evidence_description",
                      e.target.value
                    )
                  }
                  placeholder="Provide a detailed description of the evidence"
                  rows={4}
                />
              </div>

              <div className="mb-3">
                <Label for="evidence_url_1">Evidence URL 1</Label>
                <Input
                  type="text"
                  id="evidence_url_1"
                  value={resolutionForm.evidence_url_1}
                  onChange={(e) =>
                    handleResolutionFormChange("evidence_url_1", e.target.value)
                  }
                  placeholder="https://example.com/evidence1"
                />
              </div>

              <div className="mb-3">
                <Label for="evidence_url_2">Evidence URL 2 (Optional)</Label>
                <Input
                  type="text"
                  id="evidence_url_2"
                  value={resolutionForm.evidence_url_2}
                  onChange={(e) =>
                    handleResolutionFormChange("evidence_url_2", e.target.value)
                  }
                  placeholder="https://example.com/evidence2"
                />
              </div>

              {resolutionModal.error && (
                <Alert color="danger" className="mt-3 mb-0">
                  {resolutionModal.error}
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        {!resolutionModal.success && (
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => toggleResolutionModal()}
              disabled={resolutionModal.loading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSubmitResolution}
              disabled={
                resolutionModal.loading ||
                !resolutionForm.evidence_description ||
                !resolutionForm.evidence_url_1
              }
            >
              {resolutionModal.loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Submitting...
                </>
              ) : resolutionModal.isMultiResolve ? (
                "Resolve All Markets"
              ) : (
                "Submit Resolution"
              )}
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </div>
  );
};

export default EventDetail;
